/**
 * Theme Tokens - Skillfunnel Design System
 * Based on dark minimalist aesthetic with vibrant orange accents
 */

export const theme = {
  // Core Colors
  colors: {
    bg: '#000000',
    bgElevated: '#0a0a0a',
    bgHover: '#1a1a1a',
    accent: '#E67E22',
    accentDark: '#BA6316',
    accentLight: '#F39C12',
    text: '#ffffff',
    secondary: '#c0c0c0',
    textMuted: '#808080',
    border: '#333333',
  },

  // Shadows
  shadows: {
    sm: '0 2px 4px rgba(0, 0, 0, 0.3)',
    md: '0 4px 12px rgba(0, 0, 0, 0.4)',
    lg: '0 8px 32px rgba(0, 0, 0, 0.5)',
    accent: '0 0 20px rgba(230, 126, 34, 0.3)',
    accentHover: '0 0 30px rgba(230, 126, 34, 0.4)',
  },

  // Gradients
  gradients: {
    warm: 'linear-gradient(135deg, rgba(230, 126, 34, 0.1) 0%, rgba(186, 99, 22, 0.05) 100%)',
    radial: 'radial-gradient(circle at top right, rgba(230, 126, 34, 0.15), transparent 70%)',
    accent: 'linear-gradient(135deg, #E67E22 0%, #F39C12 100%)',
  },

  // Typography
  typography: {
    fontFamily: "'Grift', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', sans-serif",
    fontWeights: {
      light: 300,
      normal: 400,
      medium: 400,
      semibold: 400,
      bold: 400,
      extrabold: 400,
      black: 400,
    },
    sizes: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
      '5xl': '3rem',    // 48px
      '6xl': '3.75rem', // 60px
      '7xl': '4.5rem',  // 72px
      '8xl': '6rem',    // 96px
    },
  },

  // Spacing
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    '2xl': '3rem',   // 48px
    '3xl': '4rem',   // 64px
    '4xl': '6rem',   // 96px
    '5xl': '8rem',   // 128px
  },

  // Border Radius
  borderRadius: {
    none: '0',
    sm: '0.25rem',   // 4px
    md: '0.5rem',    // 8px
    lg: '0.75rem',   // 12px
    xl: '1rem',      // 16px
    '2xl': '1.5rem', // 24px
    full: '9999px',
  },

  // Transitions
  transitions: {
    fast: '150ms ease',
    normal: '300ms ease',
    slow: '500ms ease',
  },

  // Z-index layers
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
} as const;

// Type-safe color helper
export type ThemeColor = keyof typeof theme.colors;

// Helper functions
export const getColor = (color: ThemeColor): string => theme.colors[color];

export const rgba = (color: string, alpha: number): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// CSS variable helper for styled-components or inline styles
export const getCSSVar = (name: string): string => `var(--${name})`;

// Export individual color values for convenience
export const colors = theme.colors;
export const shadows = theme.shadows;
export const gradients = theme.gradients;
export const typography = theme.typography;
export const spacing = theme.spacing;
export const borderRadius = theme.borderRadius;
export const transitions = theme.transitions;
export const zIndex = theme.zIndex;

// Default export
export default theme;


